import * as Sentry from "@sentry/react"
import { MutationCache, QueryClient } from "@tanstack/react-query"
import {
   createTRPCClient,
   httpBatchLink,
   type TRPCClientErrorLike,
} from "@trpc/client"
import { createTRPCOptionsProxy } from "@trpc/tanstack-react-query"
import type { AppRouter } from "@unfiddle/core/trpc/types"
import { toast } from "sonner"
import superjson from "superjson"
import { CACHE_AWHILE } from "@/api"
import { env } from "@/env"

export const queryClient = new QueryClient({
   defaultOptions: {
      queries: {
         retry: (failureCount) => failureCount < 1,
         staleTime: CACHE_AWHILE,
      },
      mutations: {
         onError: (error) =>
            toast.error("Ой-ой!", {
               description: error.message,
            }),
      },
   },
   // handle hono RPC errors
   mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
         Sentry.captureException(
            error instanceof Error ? error : new Error(JSON.stringify(error)),
            {
               extra: {
                  mutationId: mutation?.mutationId,
               },
            },
         )
      },
      onSuccess: async (res) => {
         if (!(res instanceof Response)) return
         if (res.ok) return
         return Promise.reject(await res.json())
      },
   }),
})

export const trpcClient = createTRPCClient<AppRouter>({
   links: [
      httpBatchLink({
         url: `${env.API_URL}/trpc`,
         transformer: superjson,
         fetch(url, options) {
            return fetch(url, {
               ...options,
               credentials: "include",
            })
         },
      }),
   ],
})

export const trpc = createTRPCOptionsProxy<AppRouter>({
   client: trpcClient,
   queryClient,
})

export type TRPCError = TRPCClientErrorLike<AppRouter>
